import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginModal } from '../components/auth/LoginModal';
import { RegisterModal } from '../components/auth/RegisterModal';
import { EmailVerificationModal } from '../components/auth/EmailVerificationModal';
import { PasswordResetModal } from '../components/auth/PasswordResetModal';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/authStore';
import {
  BookOpenIcon,
  CalculatorIcon,
  QuestionMarkCircleIcon,
  BanknotesIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  SparklesIcon,
  ChartBarIcon,
  FlagIcon,
  BoltIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

// Unsplash royalty-free images for finance theme
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1920&q=80', // Money and calculator
  documents: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80', // Financial documents
  workflow: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80', // Dashboard analytics
  planning: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80', // Financial planning
  office: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80', // Office desk with documents
};

export const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Ensure dark mode class is removed on landing page (light theme only)
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRegistrationSuccess = (email: string) => {
    setShowRegister(false);
    setRegisteredEmail(email);
    setShowEmailVerification(true);
  };

  const handleEmailVerificationSuccess = () => {
    setShowEmailVerification(false);
    setShowLogin(true);
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setShowLogin(true);
  };

  // Education & Tools Features
  const educationFeatures = [
    {
      icon: BookOpenIcon,
      title: 'Financial Glossary',
      description:
        'Comprehensive financial terminology database with AI-powered explanations in plain language. Understand complex financial concepts easily.',
    },
    {
      icon: CalculatorIcon,
      title: 'Financial Calculator Suite',
      description:
        'Loan calculators, savings projections, retirement planning, tax estimators, and debt payoff calculators all in one place.',
    },
    {
      icon: QuestionMarkCircleIcon,
      title: 'Ask Financial Questions',
      description:
        'Get AI-powered answers to your financial questions. Learn about investing, retirement, taxes, credit, and more.',
    },
    {
      icon: BanknotesIcon,
      title: 'Budget Tracker',
      description:
        'Track income and expenses with intelligent categorization. Set budgets and get insights on your spending patterns.',
    },
    {
      icon: DocumentTextIcon,
      title: 'Statement Analyzer',
      description:
        'Upload and analyze bank statements, credit card statements, and investment reports with AI-powered insights.',
    },
    {
      icon: BriefcaseIcon,
      title: 'Portfolio Analyzer',
      description:
        'Analyze your retirement portfolio with fee breakdowns, allocation insights, and optimization suggestions.',
    },
  ];

  // Planning & Management Features
  const planningFeatures = [
    {
      icon: CreditCardIcon,
      title: 'Credit Report Interpreter',
      description:
        'Understand your credit report with AI explanations. Get personalized improvement strategies and dispute guidance.',
    },
    {
      icon: DocumentDuplicateIcon,
      title: 'Loan Application Package',
      description:
        'Prepare loan application materials with checklists, letter generation, and pre-qualification estimators.',
    },
    {
      icon: ReceiptPercentIcon,
      title: 'Tax Planning Assistant',
      description:
        'Track deductions, estimate taxes, and prepare for filing. Integration guidance for TurboTax and other tax software.',
    },
    {
      icon: ScaleIcon,
      title: 'Debt Management',
      description:
        'Track debts, compare payoff strategies (avalanche vs snowball), and visualize your path to debt freedom.',
    },
    {
      icon: SparklesIcon,
      title: 'AI Financial Advisor',
      description:
        'Get personalized financial guidance based on your situation. Comprehensive consultations on any financial topic.',
    },
  ];

  const benefits = [
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Get instant answers and analysis powered by advanced AI technology.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Secure & Private',
      description: 'Your financial data is protected with enterprise-grade security.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Easy Upload',
      description: 'Support for PDF, DOCX, images, and spreadsheet files.',
    },
    {
      icon: GlobeAltIcon,
      title: '5 Languages',
      description: 'Full support for English, Spanish, French, German, and Italian.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-amber-50/90 backdrop-blur-md border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/logo_light.png"
                alt="Auxilio"
                className="h-12"
              />
            </div>

            {/* Auth Buttons */}
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowLogin(true)}>
                Login
              </Button>
              <Button onClick={() => setShowRegister(true)}>Register</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section className="relative pt-20 pb-20 px-4 sm:px-6 lg:px-8 min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 via-amber-800/85 to-amber-700/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              {/* Title */}
              <div className="mb-6 flex items-center">
                <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">Auxilio</span>
                <img
                  src="/dollar-sign.png"
                  alt="Dollar Sign"
                  className="h-16 sm:h-20 lg:h-24 w-16 sm:w-20 lg:w-24 drop-shadow-lg"
                />
                <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-300">Finance</span>
              </div>

              <p className="text-xl sm:text-2xl text-amber-100 mb-6 max-w-xl">
                Your AI-Powered Personal Financial Advisor
              </p>

              <p className="text-lg text-amber-200 mb-8 max-w-lg">
                15+ powerful financial tools to help you understand your finances, plan for retirement,
                manage debt, prepare for taxes, and achieve your financial goals.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button size="lg" onClick={() => setShowRegister(true)} className="text-lg px-8 py-4 bg-amber-500 hover:bg-amber-600">
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => setShowLogin(true)}
                  className="text-lg px-8 py-4 bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Login to Your Account
                </Button>
              </div>
            </div>

            {/* Hero Image Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <img
                  src={IMAGES.workflow}
                  alt="Financial dashboard"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">15+</p>
                  <p className="text-sm">Financial Tools</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-emerald-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">5</p>
                  <p className="text-sm">Languages</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education & Tools Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <img
                src={IMAGES.documents}
                alt="Financial analysis"
                className="rounded-2xl shadow-xl"
              />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center mb-4">
                <div className="bg-amber-100 p-3 rounded-full mr-4">
                  <CalculatorIcon className="h-8 w-8 text-amber-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Education & Analysis Tools
                </h2>
              </div>
              <p className="text-lg text-gray-600 mb-6">
                AI-powered tools to understand your finances, analyze documents, and make informed decisions.
                Get instant insights on any financial topic.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {educationFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-amber-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planning & Management Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.planning})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="absolute inset-0 bg-gray-100/95" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Content */}
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <ChartBarIcon className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Planning & Management
                </h2>
              </div>
              <p className="text-lg text-gray-600 mb-6">
                Advanced tools to plan your financial future, manage debt,
                prepare for taxes, and achieve your goals.
              </p>
            </div>

            {/* Image */}
            <div>
              <img
                src={IMAGES.office}
                alt="Financial planning"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {planningFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-emerald-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Auxilio Finance?
            </h2>
            <p className="text-xl text-gray-600">
              Built for speed, security, and ease of use
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-amber-50 p-4 rounded-full">
                    <benefit.icon className="h-8 w-8 text-amber-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-600 to-amber-800 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/dollar-sign.png"
              alt="Get Started"
              className="h-20 w-20 brightness-0 invert opacity-90"
            />
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Take Control of Your Finances?
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            Join thousands of users who trust Auxilio Finance for their financial planning needs.
            Free to register, with no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <Button
              size="lg"
              onClick={() => setShowRegister(true)}
              className="bg-white text-amber-700 hover:bg-gray-50 text-lg px-8 py-4 font-semibold shadow-lg"
            >
              Create Free Account
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => setShowLogin(true)}
              className="bg-amber-700 text-white hover:bg-amber-600 border-white text-lg px-8 py-4"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <img src="/dollar-sign.png" alt="Auxilio Finance" className="h-8 w-8 brightness-0 invert" />
            <span className="text-xl font-bold text-white">Auxilio Finance</span>
          </div>
          <p className="text-gray-400 text-sm">
            AI-Powered Personal Financial Advisor
          </p>
          <p className="text-gray-500 text-xs mt-4 max-w-2xl mx-auto">
            Auxilio Finance provides financial information and tools for educational purposes.
            It does not constitute financial advice. Consult a qualified financial professional for personalized guidance.
          </p>
          <p className="text-gray-600 text-xs mt-4">
            Images provided by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">Unsplash</a>
          </p>
          <p className="text-gray-500 text-xs mt-2">
            &copy; {new Date().getFullYear()} Auxilio Finance. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onSwitchToPasswordReset={() => {
          setShowLogin(false);
          setShowPasswordReset(true);
        }}
      />

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={handleRegistrationSuccess}
      />

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onSuccess={handleEmailVerificationSuccess}
        email={registeredEmail}
      />

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        onSuccess={handlePasswordResetSuccess}
        onSwitchToLogin={() => {
          setShowPasswordReset(false);
          setShowLogin(true);
        }}
      />
    </div>
  );
};
